import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

type SendState = "idle" | "sending" | "sent" | "error";

/** JS-only validation (stable across browsers) */
const NAME_RE = /^[A-Za-z' -]{2,60}$/;               // letters, spaces, hyphens, apostrophes
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;   // pragmatic email
// Keep phone tolerant but sensible
const UK_PHONE_RE = /^[0-9+()\- ]{7,20}$/;

export default function Contact() {
  const [state, setState] = useState<SendState>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string; message?: string }>({});
  const formRef = useRef<HTMLFormElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);

  // Prevent accidental Enter-submit on single inputs
  useEffect(() => {
    const f = formRef.current;
    if (!f) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" && (e.target as HTMLElement)?.tagName !== "TEXTAREA") {
        // no-op (button handles disabled state)
      }
    };
    f.addEventListener("keydown", onKey);
    return () => f.removeEventListener("keydown", onKey);
  }, []);

  function validate(fields: { name: string; email: string; phone: string; message: string }) {
    const next: typeof errors = {};
    const name = fields.name.trim();
    const email = fields.email.trim();
    const phone = fields.phone.trim();
    const message = fields.message.trim();

    if (!name || !NAME_RE.test(name)) {
      next.name = "Enter your full name (letters, spaces, - or ').";
    }
    if (!email || !EMAIL_RE.test(email)) {
      next.email = "Enter a valid email address.";
    }
    // Phone required on contact page
    const phoneFlat = phone.replace(/\s+/g, "");
    if (!phone || !UK_PHONE_RE.test(phone)) {
      next.phone = "Enter a valid phone, e.g. 01522 692592 or +44 1522 692592.";
    } else if (phoneFlat.length < 10) {
      next.phone = "Phone looks too short.";
    }
    if (!message || message.length < 5) {
      next.message = "Tell us briefly what you need.";
    }
    return next;
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");
    setErrors({});

    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot: if set, pretend success
    if (String(fd.get("company") || "")) {
      setState("sent");
      form.reset();
      setConsent(false);
      return;
    }

    const payload = {
      name: String(fd.get("name") || ""),
      email: String(fd.get("email") || ""),
      phone: String(fd.get("phone") || ""),
      message: String(fd.get("message") || ""),
      source: "contact-page",
    };

    // field validation
    const v = validate(payload);
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    if (!consent) {
      setErrorMsg("Please confirm you’re happy for us to contact you.");
      return;
    }

    try {
      setState("sending");

      // 15s safety timeout
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 15000);

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(t);

      if (!res.ok) {
        // Try to parse function error payload
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          if (data?.error) msg = `${data.error}${data.reqId ? ` (reqId: ${data.reqId})` : ""}`;
        } catch {
          const text = await res.text().catch(() => "");
          if (text) msg = text;
        }
        throw new Error(msg);
      }

      setState("sent");
      form.reset();
      setConsent(false);
      setErrors({});
      requestAnimationFrame(() => statusRef.current?.focus());
    } catch (err: any) {
      setState("error");
      setErrorMsg(err?.message || "Something went wrong. Try again.");
      requestAnimationFrame(() => statusRef.current?.focus());
    }
  }

  const isBusy = state === "sending";

  return (
    <>
      {/* SEO */}
      <title>Contact | Beaumonts Plumbing &amp; Heating, Lincoln</title>
      <meta
        name="description"
        content="Contact Beaumonts Plumbing & Heating in Lincoln. Request a quote or book a survey. Call 01522 692592 or email ian@beaumontsplumbing.co.uk."
      />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_40%_at_50%_0%,#f4f7fb_0%,transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
          {/* Heading */}
          <div className="max-w-3xl">
            <p className="text-xs font-medium tracking-widest uppercase text-[#3bb3ef]">Contact</p>
            <h1 className="mt-2 text-3xl md:text-5xl font-semibold tracking-[-0.02em]">
              Tell us what you need and we’ll get it sorted
            </h1>
            <p className="mt-3 text-[15px] md:text-base text-neutral-700">
              Send the form, or use the details below. We’ll confirm dates and pricing and keep you updated.
            </p>
          </div>

          {/* Grid: form + reassurance */}
          <div className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* FORM */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.03)]">
                {state === "sent" ? (
                  <div
                    ref={statusRef}
                    tabIndex={-1}
                    aria-live="polite"
                    className="text-center py-6 outline-none"
                  >
                    <h2 className="text-lg font-semibold">Thank you</h2>
                    <p className="mt-2 text-sm text-neutral-700">
                      We’ve received your enquiry and will be in touch shortly.
                    </p>
                    <div className="mt-4 text-sm text-neutral-700">
                      Prefer to talk?{" "}
                      <a href="tel:+441522692592" className="underline">
                        01522 692592
                      </a>
                    </div>
                  </div>
                ) : (
                  <form ref={formRef} onSubmit={submit} className="grid gap-4" noValidate>
                    {/* ARIA status region for errors */}
                    <div
                      ref={statusRef}
                      tabIndex={-1}
                      aria-live="polite"
                      className="outline-none"
                    >
                      {state === "error" && (
                        <div className="mb-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                          {errorMsg || "Something went wrong. Please try again."}
                        </div>
                      )}
                    </div>

                    {/* Honeypot (hidden) */}
                    <input type="text" name="company" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

                    <div className="grid sm:grid-cols-2 gap-3">
                      {/* Name */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-neutral-800">
                          Your name*
                        </label>
                        <input
                          id="name"
                          name="name"
                          required
                          autoComplete="name"
                          maxLength={80}
                          aria-invalid={!!errors.name}
                          aria-describedby={errors.name ? "err-name" : undefined}
                          className={`mt-1 w-full border rounded px-3 py-2 text-sm ${
                            errors.name ? "border-red-500" : "border-neutral-300"
                          }`}
                          placeholder="Jane Smith"
                        />
                        {errors.name && <p id="err-name" className="mt-1 text-xs text-red-600">{errors.name}</p>}
                      </div>

                      {/* Phone */}
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-neutral-800">
                          Phone*
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          required
                          autoComplete="tel"
                          inputMode="tel"
                          maxLength={20}
                          aria-invalid={!!errors.phone}
                          aria-describedby={errors.phone ? "err-phone" : undefined}
                          className={`mt-1 w-full border rounded px-3 py-2 text-sm ${
                            errors.phone ? "border-red-500" : "border-neutral-300"
                          }`}
                          placeholder="01522 692592"
                        />
                        {errors.phone && <p id="err-phone" className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-neutral-800">
                        Email*
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        maxLength={254}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "err-email" : undefined}
                        className={`mt-1 w-full border rounded px-3 py-2 text-sm ${
                          errors.email ? "border-red-500" : "border-neutral-300"
                        }`}
                        placeholder="you@example.com"
                      />
                      {errors.email && <p id="err-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-neutral-800">
                        How can we help?*
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        minLength={5}
                        maxLength={2000}
                        aria-invalid={!!errors.message}
                        aria-describedby={errors.message ? "err-message" : "msg-hint"}
                        className={`mt-1 w-full border rounded px-3 py-2 text-sm min-h-36 ${
                          errors.message ? "border-red-500" : "border-neutral-300"
                        }`}
                        placeholder="Tell us about the job, address (LN1–LN6), and any dates that suit."
                      />
                      {!errors.message && (
                        <p id="msg-hint" className="mt-1 text-xs text-neutral-500">
                          Include postcode (LN1–LN6) and any dates that suit.
                        </p>
                      )}
                      {errors.message && <p id="err-message" className="mt-1 text-xs text-red-600">{errors.message}</p>}
                    </div>

                    {/* Consent */}
                    <label className="flex items-start gap-2 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                      />
                      I’m happy for you to contact me about this enquiry. Read our{" "}
                      <a href="/privacy" className="underline underline-offset-4 decoration-[#3bb3ef]">privacy policy</a>.
                    </label>

                    {/* Submit */}
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        type="submit"
                        disabled={isBusy}
                        className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-5 py-3 text-sm font-medium hover:brightness-110 disabled:opacity-60"
                        aria-busy={isBusy}
                      >
                        {isBusy ? "Sending…" : "Send message"}
                      </button>
                      <a
                        href="tel:+441522692592"
                        className="inline-flex items-center rounded-md border border-neutral-300 px-5 py-3 text-sm font-medium hover:bg-neutral-50"
                      >
                        Or call 01522 692592
                      </a>
                    </div>

                    <p className="mt-2 text-xs text-neutral-500">
                      We only use your details to respond to your enquiry. No marketing unless you ask us.
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* REASSURANCE / CONTACT OPTIONS */}
            <aside className="lg:col-span-5">
              <div className="rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-[0_1px_0_0_rgba(0,0,0,0.03)]">
                <h2 className="text-lg font-semibold">Prefer to talk?</h2>
                <ul className="mt-3 space-y-2 text-sm">
                  <li>
                    <a href="tel:+441522692592" className="text-neutral-900 hover:underline">01522 692592</a>{" "}
                    <span className="text-neutral-600">Landline</span>
                  </li>
                  <li>
                    <a href="tel:+447803608738" className="text-neutral-900 hover:underline">07803 608738</a>{" "}
                    <span className="text-neutral-600">Mobile</span>
                  </li>
                  <li>
                    <a href="mailto:ian@beaumontsplumbing.co.uk" className="text-neutral-900 hover:underline">
                      ian@beaumontsplumbing.co.uk
                    </a>
                  </li>
                </ul>

                <div className="mt-6">
                  <h3 className="text-sm font-medium text-neutral-900">Hours</h3>
                  <p className="mt-1 text-sm text-neutral-700">Mon–Fri 8:00–18:00, Sat 9:00–13:00</p>
                </div>

                <div className="mt-6">
                  <h3 className="text-sm font-medium text-neutral-900">Areas we cover</h3>
                  <p className="mt-1 text-sm text-neutral-700">Lincoln and surrounding postcodes LN1–LN6.</p>
                  <Link to="/areas" className="mt-2 inline-flex text-sm text-[#1f8fd3] hover:underline">
                    View coverage &rarr;
                  </Link>
                </div>

                <div className="mt-6 rounded-xl overflow-hidden ring-1 ring-neutral-200">
                  <iframe
                    title="Lincoln area map"
                    src="https://www.google.com/maps?q=Lincoln,UK&z=11&output=embed"
                    className="w-full h-[220px] md:h-[260px] border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {[
                    "Itemised quotes that match invoices",
                    "Registered and insured engineers",
                    "Clean, tidy working areas",
                    "Warranty and aftercare",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-2 text-neutral-700">
                      <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-[#3bb3ef]" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
